<script setup lang="ts">
import { useUser } from "@/composables/user/user";
import { useRoute } from "vue-router";

const FooterFull = useAsyncComponent(() => import("@/components/layouts/shared/FooterFull.vue"));
const FooterSimple = useAsyncComponent(() => import("@/components/layouts/shared/FooterSimple.vue"));
const Topbar = useAsyncComponent(() => import("@/components/layouts/shared/topbar/Topbar.vue"));
const DialogAuth = useAsyncComponent(() => import("./shared/AuthDialog.vue"));
const Breadcrumbs = useAsyncComponent(() => import("./shared/Breadcrumbs.vue"));
const AdminSecretPanel = useAsyncComponent(() => import("./shared/AdminSecretPanel.vue"));

const { user, isAdmin } = useUser();
const route = useRoute();

const renderSimpleFooter = computed(() => route.meta.renderSimpleFooter === true);

const pageWrapperCssClasses = computed(() => ({
  page: true,
  "page--gray-bg": route.meta.grayBackground === true,
}));
</script>

<template>
  <div id="top" :class="pageWrapperCssClasses">
    <a href="#main-content" class="skip-link">{{ $gettext("Přeskočit na hlavní obsah") }}</a>

    <Topbar />

    <main id="main-content" class="page__content">
      <Breadcrumbs />

      <RouterView v-slot="{ Component }" name="mainContent">
        <transition name="fade" mode="out-in">
          <div :key="route.name">
            <component :is="Component" />
          </div>
        </transition>
      </RouterView>
    </main>

    <div class="page__footer-wrapper">
      <FooterSimple v-if="renderSimpleFooter" />
      <FooterFull v-else />
    </div>

    <DialogAuth v-if="!user" />

    <div id="toast-live-region" role="status" aria-live="polite" aria-atomic="true" class="sr-only"></div>

    <AdminSecretPanel v-if="isAdmin" />
  </div>
</template>

<style lang="scss" scoped>
.page {
  min-height: 100vh;
  width: 100vw;
  background-color: var(--white);
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease;

  &--gray-bg {
    // Exposed so components blending into the page ground (e.g. the tab bar's edge fades) can match it.
    --pageGroundColor: var(--gray50);
    background-color: var(--pageGroundColor);
  }

  &__content {
    padding-top: var(--topbarHeight);
    width: 100%;
  }

  // Skip link: visually hidden until focused, allows keyboard users to skip navigation
  // Uses clip-path instead of translateY(-100%) to avoid appearing during Mac elastic overscroll
  .skip-link {
    position: absolute;
    top: 0;
    left: 0;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    width: 1px;
    height: 1px;
    overflow: hidden;
    white-space: nowrap;
    background: var(--greenPrimary);
    color: var(--white);
    padding: var(--spacing2x) var(--spacing3x);
    text-decoration: none;
    z-index: 9999;
    border-radius: 0 0 var(--radius4x) 0;

    &:focus {
      clip: auto;
      clip-path: none;
      width: auto;
      height: auto;
      overflow: visible;
      white-space: normal;
    }
  }
  &__footer-wrapper {
    margin-top: auto;
  }
}
</style>
